import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardList, Printer, Save } from 'lucide-react';
import { toast } from 'sonner';
import {
  getCurrentProject,
  getProject,
  listProjects,
  renameProject,
} from '../utils/api';
import { useT } from '../i18n';

// Ce que le registre garde au plus. Le serveur l'annonce avec `truncated`,
// donc ce nombre ne sert qu'à écrire la phrase — il n'est pas la limite.
const KEPT_STEPS = 300;

// Ce qu'une opération a nommé, en lignes plutôt qu'en JSON : ce document part
// chez un client. Les listes vides sont écartées — « aucune valeur sans
// correspondance » n'a rien à dire ; un zéro, si.
//
// Les noms de champ sont ceux du rapport lui-même. Les traduire demanderait
// une clé par champ de chaque opération ; la phrase de l'étape, elle, est déjà
// dans la langue du lecteur et porte les comptes.
const LISTED_AT_MOST = 50;

export const readable = (outcome) =>
  Object.entries(outcome || {})
    .filter(([, value]) => !(Array.isArray(value) && value.length === 0))
    .map(([field, value]) => [
      field.replace(/_/g, ' '),
      Array.isArray(value)
        ? value.slice(0, LISTED_AT_MOST).join(', ')
          + (value.length > LISTED_AT_MOST ? ` … (+${value.length - LISTED_AT_MOST})` : '')
        : String(value),
    ]);

/**
 * Le registre d'un projet, étape par étape.
 *
 * Il existe pour une raison précise : rendre compte à un client de ce qu'on a
 * fait à ses données. Ce n'est donc ni l'historique — qui sert à annuler et
 * meurt avec la session — ni une recette, qui garde les arguments pour rejouer
 * mais aucun résultat. Rejouable n'est pas vérifiable.
 *
 * Rien à créer, rien à cocher : chaque opération s'y inscrit toute seule. Le
 * projet naît avec le nom du fichier, et se renomme, parce qu'un client ne
 * s'appelle pas carnet4.xlsx.
 */
const ProjectRecord = () => {
  const t = useT();
  const [project, setProject] = useState(null);
  const [others, setOthers] = useState([]);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  // Un `<details>` fermé reste fermé à l'impression : le CSS ne peut pas
  // l'ouvrir, et le détail est précisément ce que le client vérifie. Il est
  // donc ouvert avant d'imprimer — au clic du bouton, et aussi sur Ctrl+P.
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [current, all] = await Promise.all([getCurrentProject(), listProjects()]);
      setProject(current.project);
      setName(current.project?.name || '');
      setOthers((all.projects || []).filter(
        (entry) => entry.project_id !== current.project?.project_id));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('project.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const expand = () => setExpanded(true);
    window.addEventListener('beforeprint', expand);
    return () => window.removeEventListener('beforeprint', expand);
  }, []);

  const print = () => {
    setExpanded(true);
    // Un tour de boucle, pour que le rendu ouvert soit celui qui part sur la
    // page plutôt que celui d'avant.
    setTimeout(() => window.print(), 0);
  };

  const open = async (projectId) => {
    try {
      const result = await getProject(projectId);
      setProject(result.project);
      setName(result.project.name);
      setOthers((current) => current.filter((entry) => entry.project_id !== projectId));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('project.loadFailed'));
    }
  };

  const rename = async () => {
    try {
      await renameProject(project.project_id, name);
      setProject((current) => ({ ...current, name: name.trim() }));
      toast.success(t('project.renamed'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('project.loadFailed'));
    }
  };

  /**
   * La phrase de l'étape, écrite dans la langue du lecteur.
   *
   * Le serveur envoie un code et ses paramètres — pas une phrase. Une entrée
   * sans code garde celle avec laquelle elle a été enregistrée : de l'anglais,
   * et vrai, ce qui vaut mieux qu'une phrase inventée.
   */
  const say = (entry) => (entry.code ? t(entry.code, entry.params) : entry.label);

  const shapeChange = (entry, previous) => {
    if (!previous || previous.rows === entry.rows) return null;
    const delta = entry.rows - previous.rows;
    return delta < 0
      ? t('project.rowsWent', { count: -delta })
      : t('project.rowsCame', { count: delta });
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">{t('project.subtitle')}</p>;
  }

  if (!project) {
    return (
      <div className="text-center py-12" data-testid="project-none">
        <ClipboardList className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground">{t('project.noFile')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 no-print">
        <div>
          <h2 className="text-xl font-semibold">{t('project.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('project.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={print}
          className="btn-secondary flex items-center gap-2 flex-shrink-0"
          data-testid="project-print"
        >
          <Printer className="h-4 w-4" />
          {t('project.print')}
        </button>
      </div>

      <div className="flex gap-2 no-print">
        <input
          type="text"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('project.namePlaceholder')}
          className="input flex-1"
          data-testid="project-name"
        />
        <button
          type="button"
          onClick={rename}
          disabled={!name.trim() || name.trim() === project.name}
          className="btn-secondary flex items-center gap-2"
          data-testid="project-rename"
        >
          <Save className="h-4 w-4" />
          {t('project.rename')}
        </button>
      </div>

      {/* Ce qui part à l'impression : le document que le client reçoit. */}
      <div className="card p-6 space-y-4 print-target" data-testid="project-record">
        <div className="border-b border-border pb-3">
          <h3 className="text-lg font-semibold">{project.name}</h3>
          <p className="text-xs text-muted-foreground">
            {project.source_filename} · {t('project.shape', {
              rows: project.rows ?? 0, columns: project.columns ?? 0 })}
          </p>
        </div>

        {project.truncated && (
          <p className="text-xs text-amber-800" data-testid="project-truncated">
            {t('project.truncated', { max: KEPT_STEPS })}
          </p>
        )}

        {project.entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('project.empty')}</p>
        ) : (
          <ol className="space-y-4">
            {project.entries.map((entry, index) => {
              const change = shapeChange(entry, project.entries[index - 1]);
              return (
                <li key={index} className="text-sm" data-testid={`project-step-${index}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium">
                      {t('project.step', { number: index + 1 })} — {say(entry)}
                    </span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(entry.at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('project.shape', { rows: entry.rows, columns: entry.columns })}
                    {change ? ` · ${change}` : ''}
                  </p>
                  {entry.outcome?.detail_dropped && (
                    <p className="text-xs text-amber-800">{t('project.detailDropped')}</p>
                  )}
                  {entry.outcome && !entry.outcome.detail_dropped && (
                    <details className="mt-1" open={expanded}>
                      <summary className="text-xs text-muted-foreground cursor-pointer">
                        {t('project.detail')}
                      </summary>
                      <dl className="text-xs bg-muted/40 rounded p-2 mt-1 space-y-0.5">
                        {readable(entry.outcome).map(([field, value]) => (
                          <div key={field} className="flex gap-2">
                            <dt className="text-muted-foreground flex-shrink-0">{field}</dt>
                            <dd className="break-all">{value}</dd>
                          </div>
                        ))}
                      </dl>
                    </details>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {others.length > 0 && (
        <div className="card p-4 no-print">
          <h3 className="text-sm font-semibold mb-2">{t('project.others')}</h3>
          <ul className="space-y-1">
            {others.map((entry) => (
              <li key={entry.project_id} className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate">
                  {entry.name}
                  <span className="text-xs text-muted-foreground ml-2">
                    {entry.step_count}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => open(entry.project_id)}
                  className="btn-ghost text-xs flex-shrink-0"
                  data-testid={`project-open-${entry.project_id}`}
                >
                  {t('project.openOther')}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ProjectRecord;
